import {
  isTpvcaMandatorySlug,
} from '@/utils/tpvcaInsurance';
import { isKaskoInsuranceSlug } from '@/utils/kaskoInsurance';
import { isTravelInsuranceSlug } from '@/utils/travelInsurance';
// context/insuranceContext.js
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { useSelector } from 'react-redux';
import { usePathname } from 'next/navigation';
import {
  addTimestampToStorage,
  isStorageExpired,
  cleanExpiredStorage,
  initializeStorageCleanup,
  clearStorageForNewQuote,
  getTodayFormatted,
  getTomorrowFormatted,
  isStartDateBeforeMinValid,
  hasMeaningfulCalculatorData,
  persistOffersCalculationInterrupted,
} from '@/utils/insuranceStorageCleanup';
import { cancelQuoteSubmit } from '@/utils/quoteSubmitCancellation';
import { getVehicleFlowRedirectStep } from '@/utils/insuranceStepAccess';
import { isBicycleInsuranceSlug } from '@/utils/bicycleInsurance';
import {
  isHealthInsuranceSlug,
  isRoadsideInsuranceSlug,
  isPavezejoInsuranceSlug,
} from '@/utils/calculatorProductSlugs';
import { delocalizePath } from '@/lib/localizedRoutes.mjs';
import { getClientInfo } from '@/utils/api';
import { isAuthenticated } from '@/utils/auth';
import {
  readSelfServiceEmailFromStorage,
  selfServiceEmailFromProfile,
  withLockedAccountEmail,
} from '@/utils/selfServiceAccountEmail';

const InsuranceContext = createContext(null);

function parseStoredCalculatorStep(value) {
  const step = Number(value);
  if (!Number.isInteger(step) || step < 0 || step > 10) return 0;
  return step;
}

/** TPVCA: „Pagalba kelyje“ is chosen per session — do not restore from localStorage. */

const CALCULATOR_REFRESH_CONFIRM_MSG =
  'Ar tikrai norite atnaujinti puslapį? Jūsų įvesti duomenys gali būti prarasti.';

/** Internal step index for the offers page (Step4Offers) per insurance slug. */
function getOffersInternalStep(slug) {
  if (
    isTpvcaMandatorySlug(slug) ||
    isPavezejoInsuranceSlug(slug) ||
    isKaskoInsuranceSlug(slug) ||
    isTravelInsuranceSlug(slug)
  ) {
    return 3;
  }
  return null;
}

function pickFormDataForStorage(formData, insuranceSlug) {
  const { agree, agreed, ...rest } = formData;
  let payload = rest;
  if (isTpvcaMandatorySlug(insuranceSlug)) {
    const { technicalAssistancePackage: _ta, ...withoutTa } = payload;
    payload = withoutTa;
  }
  // While offers are being recalculated, do not persist stale/mixed quote results.
  if (payload.quoteRecalculationInProgress) {
    const {
      completedQuoteResponse: _cqr,
      selectedOffer: _so,
      selectedProviders: _sp,
      ...withoutOffers
    } = payload;
    return withoutOffers;
  }
  return payload;
}

export function InsuranceProvider({
  children,
  insuranceData: providedInsuranceData,
}) {
  const publicPathname = usePathname();
  const pathname = useMemo(
    () => delocalizePath(publicPathname || '/'),
    [publicPathname]
  );

  // Strategy 2: Initialize storage cleanup on mount (time-based expiration)
  useEffect(() => {
    const cleanup = initializeStorageCleanup();
    // Return cleanup function to clear interval on unmount
    return cleanup;
  }, []);

  // Step lives in localStorage. Start at 0 so server and client markup match,
  // then restore the saved step in the layout effect below.
  const [step, setStepState] = useState(0);
  const stepRef = useRef(0);

  // Track previous pathname to detect insurance type changes
  // Initialize to null so we can detect initial mount vs pathname change
  const prevPathnameRef = useRef(null);

  // Get localStorage key based on pathname (insurance type)
  const getStorageKey = useCallback(() => {
    // Extract insurance type from pathname (e.g., /skaiciuokle/automobilio-privalomasis-draudimas-internetu)
    const pathParts = pathname.split('/');
    const insuranceSlug = pathParts[pathParts.length - 1] || 'default';
    return `insurance_calculator_${insuranceSlug}`;
  }, [pathname]);

  // Load formData from localStorage
  const loadFormDataFromStorage = useCallback(() => {
    try {
      const storageKey = getStorageKey();
      const savedData = localStorage.getItem(storageKey);
      if (savedData) {
        const parsed = JSON.parse(savedData);

        // Strategy 2: Check if data has expired (time-based cleanup)
        if (isStorageExpired(parsed)) {
          localStorage.removeItem(storageKey);
          return {
            duration: '12',
            usagePurpose: 'NONE',
          };
        }

        // Merge with defaults to ensure required fields exist
        // Exclude agreement checkboxes - they should always be unchecked on page load
        const { agree, agreed, ...parsedWithoutAgreement } = parsed;
        const merged = {
          duration: '12',
          usagePurpose: 'NONE',
          ...parsedWithoutAgreement,
        };
        const slug = pathname?.split('/').pop() || '';
        // Health / bicycle / roadside: default / clamp start to tomorrow; others: at least today
        if (
          isHealthInsuranceSlug(slug) ||
          isBicycleInsuranceSlug(slug) ||
          isRoadsideInsuranceSlug(slug)
        ) {
          if (!merged.startDate || merged.startDate <= getTodayFormatted()) {
            merged.startDate = getTomorrowFormatted();
          }
        } else if (
          merged.startDate &&
          isStartDateBeforeMinValid(merged.startDate)
        ) {
          merged.startDate = getTodayFormatted();
        }
        if (isTpvcaMandatorySlug(slug)) {
          merged.technicalAssistancePackage = null;
        }
        // Legacy typo — API enum is BUSINESS (TPVCA / KASKO)
        if (
          (isTpvcaMandatorySlug(slug) || isKaskoInsuranceSlug(slug)) &&
          merged.usagePurpose === 'BUSSINESS'
        ) {
          merged.usagePurpose = 'BUSINESS';
        }
        // Travel: clamp stale past dates from localStorage (same idea as startDate)
        if (isTravelInsuranceSlug(slug)) {
          const today = getTodayFormatted();
          if (
            merged.travel_start_date &&
            String(merged.travel_start_date).split('T')[0] < today
          ) {
            merged.travel_start_date = today;
          }
          const startYmd = merged.travel_start_date
            ? String(merged.travel_start_date).split('T')[0]
            : '';
          if (
            merged.travel_end_date &&
            startYmd &&
            String(merged.travel_end_date).split('T')[0] <= startYmd
          ) {
            const [y, m, d] = startYmd.split('-').map(Number);
            const next = new Date(y, m - 1, d + 1);
            merged.travel_end_date = `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-${String(next.getDate()).padStart(2, '0')}`;
          }
        }
        // Kasko UI no longer offers BUSINESS; map stale storage to NONE
        if (isKaskoInsuranceSlug(slug) && merged.usagePurpose === 'BUSINESS') {
          merged.usagePurpose = 'NONE';
        }
        // Kasko main_deductable options narrowed (no 175 / 250 / 450)
        if (isKaskoInsuranceSlug(slug)) {
          const md = merged.mainDeductable;
          if (
            md != null &&
            md !== '' &&
            ['175', '250', '450'].includes(String(md))
          ) {
            merged.mainDeductable = '';
          }
          // Kasko: fixed 12-month term (no duration picker)
          if (merged.duration !== '12') {
            merged.duration = '12';
          }
        }
        delete merged.calculatorStep;
        return merged;
      }
    } catch (error) {
      console.error('Error loading formData from localStorage:', error);
    }
    return {
      duration: '12', // Default to 12 months
      usagePurpose: 'NONE', // Default to no adjustment
    };
  }, [getStorageKey, pathname]);

  const loadStoredCalculatorStep = useCallback(() => {
    if (typeof window === 'undefined') return 0;
    try {
      const savedData = localStorage.getItem(getStorageKey());
      if (!savedData) return 0;
      const parsed = JSON.parse(savedData);
      if (isStorageExpired(parsed)) return 0;
      return parseStoredCalculatorStep(parsed.calculatorStep);
    } catch {
      return 0;
    }
  }, [getStorageKey]);

  const persistCalculatorStep = useCallback(
    stepNumber => {
      if (typeof window === 'undefined' || !pathname?.includes('/skaiciuokle/')) {
        return;
      }
      const safeStep = parseStoredCalculatorStep(stepNumber);
      try {
        const storageKey = getStorageKey();
        let base = {};
        const savedData = localStorage.getItem(storageKey);
        if (savedData) {
          const parsed = JSON.parse(savedData);
          if (isStorageExpired(parsed)) {
            localStorage.removeItem(storageKey);
          } else {
            base = parsed;
          }
        }
        if (base.calculatorStep === safeStep && Object.keys(base).length > 0) {
          return;
        }
        localStorage.setItem(
          storageKey,
          JSON.stringify(
            addTimestampToStorage({ ...base, calculatorStep: safeStep })
          )
        );
      } catch (error) {
        console.error('Error saving calculator step:', error);
      }
    },
    [getStorageKey, pathname]
  );

  // Initialize formData with consistent defaults for both server and client.
  // Loading from localStorage in useState causes hydration mismatch (server has defaults,
  // client has persisted data). We load from localStorage in useEffect instead.
  const [formData, setFormDataState] = useState({
    duration: '12',
    usagePurpose: 'NONE',
  });
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const selfServiceAccountEmailRef = useRef(null);
  const [selfServiceAccountEmail, setSelfServiceAccountEmail] = useState(null);
  const setFormData = useCallback(updater => {
    setFormDataState(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      const stamped = withLockedAccountEmail(
        next,
        selfServiceAccountEmailRef.current
      );
      formDataRef.current = stamped;
      return stamped;
    });
  }, []);
  const isLoadingRef = useRef(false);
  const quoteCalculationActiveRef = useRef(false);
  /** Skip one save after hydration — first save effect still sees pre-load formData. */
  const skipSaveUntilHydratedRef = useRef(true);
  /** Skip refresh warning when leaving calculator intentionally (e.g. payment redirect). */
  const allowCalculatorUnloadRef = useRef(false);
  const unloadGuardPathnameRef = useRef(null);
  /**
   * Chrome only shows the reload-button dialog after a real user gesture (sticky activation).
   * Tracked via pointer/keyboard/input on the page — not from localStorage hydration alone.
   */
  const calculatorUserInteractedRef = useRef(false);
  const [apiError, setApiError] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [isLoading, setIsLoadingState] = useState(false);
  const setIsLoading = useCallback(value => {
    const next = typeof value === 'function' ? value(isLoadingRef.current) : value;
    isLoadingRef.current = next;
    setIsLoadingState(next);
  }, []);
  const [stepSubmitHandler, setStepSubmitHandler] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const { plateNumber } = useSelector(state => state.insurance);

  const beginOffersRecalculation = useCallback(() => {
    quoteCalculationActiveRef.current = true;
    isLoadingRef.current = true;
    setIsLoadingState(true);
  }, []);

  const endOffersRecalculation = useCallback(() => {
    quoteCalculationActiveRef.current = false;
    isLoadingRef.current = false;
    setIsLoadingState(false);
  }, []);

  const allowCalculatorUnload = useCallback(() => {
    allowCalculatorUnloadRef.current = true;
  }, []);

  // AbortController for canceling ongoing API requests
  const abortControllerRef = useRef(null);

  // Keep stepRef in sync with step state
  useEffect(() => {
    stepRef.current = step;
  }, [step]);

  // Desktop scroll on step change is handled by InsuranceStepper (instant scroll to top).

  // Old links and bookmarks may still include ?s=. Drop it without changing the step.
  useEffect(() => {
    if (!pathname?.includes('/skaiciuokle/') || typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (!params.has('s')) return;
    params.delete('s');
    const search = params.toString();
    const newUrl = search ? `${publicPathname}?${search}` : publicPathname;
    window.history.replaceState(window.history.state ?? {}, '', newUrl);
  }, [pathname, publicPathname]);

  // Refresh guard on every /skaiciuokle/ step:
  // - F5 / Ctrl+R → Lithuanian window.confirm (custom text)
  // - Browser reload button → native “Reload site?” (browsers block custom alert there)
  useLayoutEffect(() => {
    if (!pathname?.includes('/skaiciuokle/')) {
      unloadGuardPathnameRef.current = null;
      window.onbeforeunload = null;
      return;
    }

    if (unloadGuardPathnameRef.current !== pathname) {
      allowCalculatorUnloadRef.current = false;
      calculatorUserInteractedRef.current = false;
      unloadGuardPathnameRef.current = pathname;
    }

    const insuranceSlug = pathname.split('/').pop() || '';

    const markUserInteracted = () => {
      calculatorUserInteractedRef.current = true;
    };

    const persistInterruptedRecalculationIfNeeded = () => {
      if (
        quoteCalculationActiveRef.current ||
        formDataRef.current.quoteRecalculationInProgress
      ) {
        persistOffersCalculationInterrupted(
          insuranceSlug,
          formDataRef.current
        );
      }
    };

    const isRefreshShortcut = event => {
      if (event.key === 'F5') return true;
      if (
        (event.key === 'r' || event.key === 'R') &&
        (event.ctrlKey || event.metaKey)
      ) {
        return true;
      }
      return false;
    };

    const shouldWarnBeforeRefresh = () => {
      if (allowCalculatorUnloadRef.current) return false;
      return (
        calculatorUserInteractedRef.current ||
        isLoadingRef.current ||
        quoteCalculationActiveRef.current ||
        formDataRef.current.quoteRecalculationInProgress === true ||
        hasMeaningfulCalculatorData(formDataRef.current)
      );
    };

    // window.onbeforeunload is more reliable for the toolbar reload button than addEventListener.
    window.onbeforeunload = event => {
      if (!shouldWarnBeforeRefresh()) return;

      persistInterruptedRecalculationIfNeeded();
      event.preventDefault();
      event.returnValue = '';
      return '';
    };

    const handleKeyDown = event => {
      if (!isRefreshShortcut(event)) return;
      if (!shouldWarnBeforeRefresh()) return;

      markUserInteracted();
      event.preventDefault();
      event.stopImmediatePropagation();

      if (!window.confirm(CALCULATOR_REFRESH_CONFIRM_MSG)) return;

      persistInterruptedRecalculationIfNeeded();
      // User already confirmed — skip the second native beforeunload dialog on reload.
      allowCalculatorUnloadRef.current = true;
      window.location.reload();
    };

    const interactionEvents = [
      'pointerdown',
      'keydown',
      'input',
      'change',
      'touchstart',
    ];
    interactionEvents.forEach(type => {
      document.addEventListener(type, markUserInteracted, {
        capture: true,
        passive: true,
      });
    });
    document.addEventListener('keydown', handleKeyDown, { capture: true });

    return () => {
      window.onbeforeunload = null;
      interactionEvents.forEach(type => {
        document.removeEventListener(type, markUserInteracted, { capture: true });
      });
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [pathname, step]);

  // Handle browser back/forward buttons with confirmation
  useEffect(() => {
    let isHandlingPopState = false;

    const handlePopState = () => {
      // Prevent infinite loops
      if (isHandlingPopState) {
        return;
      }

      if (!pathname?.includes('/skaiciuokle/')) return;

      const currentStep = stepRef.current;
      // Step changes stay in the app. Browser back leaves the calculator.
      if (currentStep === 0) return;

      isHandlingPopState = true;
      const confirmed = window.confirm(
        'Ar tikrai norite grįžti atgal? Jūsų įvesti duomenys gali būti prarasti.'
      );

      if (!confirmed) {
        const restoreParams = new URLSearchParams(window.location.search);
        restoreParams.delete('s');
        const search = restoreParams.toString();
        const newUrl = search ? `${publicPathname}?${search}` : publicPathname;
        window.history.pushState({ step: currentStep }, '', newUrl);
      }
      isHandlingPopState = false;
    };

    // Listen for popstate (back/forward button)
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [pathname, publicPathname]);

  const setStep = useCallback(
    newStep => {
      const stepNumber = parseStoredCalculatorStep(
        typeof newStep === 'function' ? newStep(stepRef.current) : newStep
      );
      if (stepNumber === stepRef.current) return;
      stepRef.current = stepNumber;
      setStepState(stepNumber);
      persistCalculatorStep(stepNumber);
    },
    [persistCalculatorStep]
  );

  // Save formData to localStorage whenever it changes (only for calculator routes)
  useEffect(() => {
    // Only save if we're in the calculator route
    if (!pathname || !pathname.includes('/skaiciuokle/')) {
      return;
    }

    // Skip one save after storage hydration — that effect still sees pre-load formData.
    if (skipSaveUntilHydratedRef.current) {
      skipSaveUntilHydratedRef.current = false;
      return;
    }

    try {
      const storageKey = getStorageKey();
      // Save all formData including API responses (estimate and quote results)
      // These are needed to restore the state after page refresh, especially on step 8 (summary)
      // Exclude agreement checkboxes - they should not be persisted
      const insuranceSlug = pathname.split('/').pop() || '';
      const payload = pickFormDataForStorage(formData, insuranceSlug);
      // Strategy 2: Add timestamp for expiration tracking
      const dataToSave = addTimestampToStorage({
        ...payload,
        calculatorStep: stepRef.current,
      });

      // Try to save to localStorage
      const serialized = JSON.stringify(dataToSave);
      localStorage.setItem(storageKey, serialized);
    } catch (error) {
      // Handle QuotaExceededError - localStorage has a ~5-10MB limit
      if (
        error.name === 'QuotaExceededError' ||
        error.name === 'NS_ERROR_DOM_QUOTA_REACHED'
      ) {
        console.warn(
          'localStorage quota exceeded. Data is too large to save completely.',
          'Some data may be lost on page refresh.'
        );
        // Still try to save essential data without the largest objects
        try {
          const insuranceSlug = pathname.split('/').pop() || '';
          const essentialData = pickFormDataForStorage(formData, insuranceSlug);
          // Try saving again - if it still fails, at least we tried
          localStorage.setItem(
            storageKey,
            JSON.stringify(
              addTimestampToStorage({
                ...essentialData,
                calculatorStep: stepRef.current,
              })
            )
          );
        } catch (fallbackError) {
          console.error(
            'Could not save formData to localStorage:',
            fallbackError
          );
        }
      } else {
        console.error('Error saving formData to localStorage:', error);
      }
    }
  }, [formData, getStorageKey, pathname]);

  // Load formData from localStorage when pathname changes (different insurance type)
  // Also reload on mount to ensure fresh data from localStorage (handles page refreshes)
  //
  // useLayoutEffect (not useEffect): must run before the save effect below. On mount, the
  // save effect would otherwise run first with default formData and overwrite localStorage
  // before we read it — refresh would lose plate, code, step fields, etc.
  useLayoutEffect(() => {
    // Only load if we're in the calculator route
    if (!pathname || !pathname.includes('/skaiciuokle/')) {
      prevPathnameRef.current = pathname;
      return;
    }

    // Load if this is initial mount (prevPathnameRef is null) or pathname changed
    // This ensures data is loaded on page refresh even if pathname hasn't changed
    if (
      prevPathnameRef.current === null ||
      prevPathnameRef.current !== pathname
    ) {
      skipSaveUntilHydratedRef.current = true;
      const loadedData = loadFormDataFromStorage();
      const insuranceSlug = pathname.split('/').pop() || '';
      let nextStep = loadStoredCalculatorStep();

      // Refresh during offers recalculation: return to the additional-data step
      // so stale/mixed completedQuoteResponse is not shown on the offers step.
      if (loadedData.quoteRecalculationInProgress) {
        const offersStep = getOffersInternalStep(insuranceSlug);
        if (offersStep != null) nextStep = offersStep - 1;
        const restoredData = {
          ...loadedData,
          quoteRecalculationInProgress: false,
          completedQuoteResponse: null,
          initialQuoteResponse: null,
          selectedOffer: null,
          selectedProviders: [],
        };
        quoteCalculationActiveRef.current = false;
        isLoadingRef.current = false;
        setIsLoadingState(false);
        stepRef.current = nextStep;
        setStepState(nextStep);
        setFormData(restoredData);
        prevPathnameRef.current = pathname;
        calculatorUserInteractedRef.current = true;
        return;
      }

      const vehicleRedirect = getVehicleFlowRedirectStep(
        loadedData,
        insuranceSlug,
        nextStep
      );
      if (vehicleRedirect != null) {
        nextStep = vehicleRedirect;
      } else if (!hasMeaningfulCalculatorData(loadedData) && nextStep > 0) {
        nextStep = 0;
      }

      stepRef.current = nextStep;
      setStepState(nextStep);
      // Always update with loaded data (it will have defaults if nothing was saved)
      // This ensures vehicle info (vehicleInfo, carInfo, carDetailsResponse) and other data is restored on page refresh
      setFormData(loadedData);
      prevPathnameRef.current = pathname;
      if (hasMeaningfulCalculatorData(loadedData)) {
        calculatorUserInteractedRef.current = true;
      }
    }
  }, [pathname, loadFormDataFromStorage, loadStoredCalculatorStep, setFormData]);

  // Deep link / stale session on TA, personal, loader, or summary without prior steps → step 0
  useLayoutEffect(() => {
    if (!pathname?.includes('/skaiciuokle/')) return;

    const insuranceSlug = pathname.split('/').pop() || '';
    const redirectStep = getVehicleFlowRedirectStep(
      formDataRef.current,
      insuranceSlug,
      stepRef.current
    );
    if (redirectStep == null || stepRef.current === redirectStep) return;

    setStep(redirectStep);
  }, [step, formData, pathname, setStep]);

  // Periodic cleanExpiredStorage removes keys while the tab can still show old step — reset to step 1
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleStorageCleared = e => {
      const slugs = e.detail?.insuranceSlugs;
      if (!Array.isArray(slugs) || !pathname?.includes('/skaiciuokle/')) return;
      const insuranceSlug = pathname.split('/').pop();
      if (!insuranceSlug || !slugs.includes(insuranceSlug)) return;

      setFormData({
        duration: '12',
        usagePurpose: 'NONE',
      });
      if (stepRef.current > 0) {
        setStep(0);
      }
    };

    window.addEventListener(
      'insuranceCalculatorStorageCleared',
      handleStorageCleared
    );
    return () =>
      window.removeEventListener(
        'insuranceCalculatorStorageCleared',
        handleStorageCleared
      );
  }, [pathname, setStep, setFormData]);

  useEffect(() => {
    if (plateNumber) {
      setFormData(prev => ({ ...prev, plate: plateNumber }));
    }
  }, [plateNumber]);

  // A signed-in purchase is stored on the account email.
  // Prefill that address and ignore a different value typed in the calculator.
  useEffect(() => {
    let cancelled = false;

    const publish = email => {
      const next = email || null;
      selfServiceAccountEmailRef.current = next;
      setSelfServiceAccountEmail(prev => (prev === next ? prev : next));
      if (next) setFormData(prev => prev);
    };

    const refresh = async () => {
      if (!isAuthenticated()) {
        publish(null);
        return;
      }

      const optimistic = readSelfServiceEmailFromStorage();
      if (optimistic) publish(optimistic);

      try {
        const user = await getClientInfo();
        if (cancelled) return;
        publish(selfServiceEmailFromProfile(user));
      } catch {
        if (cancelled) return;
        if (!optimistic) publish(null);
      }
    };

    refresh();
    window.addEventListener('localStorageChange', refresh);
    window.addEventListener('userDataUpdated', refresh);
    return () => {
      cancelled = true;
      window.removeEventListener('localStorageChange', refresh);
      window.removeEventListener('userDataUpdated', refresh);
    };
  }, [setFormData]);

  const retryApiRequest = () => {
    setIsRetrying(true);
    setIsLoading(true); // Set global loading state
    setApiError(null);
    // Reset the completed quote response to trigger a new request
    setFormData(prev => ({ ...prev, completedQuoteResponse: null }));
  };

  // Cancel ongoing API requests
  const cancelOngoingRequests = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  /** Abort quote estimate/polling and reset loading — e.g. sidebar edit during offers loader. */
  const cancelActiveQuoteCalculation = useCallback(() => {
    cancelQuoteSubmit();
    cancelOngoingRequests();
    endOffersRecalculation();
    isLoadingRef.current = false;
    setIsLoadingState(false);
    setApiError(null);
    setFormData(prev => {
      const status = prev.completedQuoteResponse?.status;
      const keepCompleted =
        status === 'completed' || status === 'complete';
      if (keepCompleted) {
        return {
          ...prev,
          quoteRecalculationInProgress: false,
        };
      }
      return {
        ...prev,
        quoteRecalculationInProgress: false,
        completedQuoteResponse: null,
        initialQuoteResponse: null,
        selectedOffer: null,
        selectedProviders: [],
      };
    });
  }, [cancelOngoingRequests, endOffersRecalculation, setFormData]);

  // Create a new abort controller for new requests
  const createAbortController = useCallback(() => {
    // Cancel any existing requests first
    cancelOngoingRequests();
    // Create a new abort controller
    abortControllerRef.current = new AbortController();
    return abortControllerRef.current;
  }, [cancelOngoingRequests]);

  // Get current abort signal
  const getAbortSignal = useCallback(() => {
    if (!abortControllerRef.current) {
      abortControllerRef.current = new AbortController();
    }
    return abortControllerRef.current.signal;
  }, []);

  /** Stop in-flight offers fetch/recalculate and return to additional-data step (?s=3). */
  const cancelOffersRecalculation = useCallback(() => {
    const insuranceSlug = pathname?.split('/').pop() || '';
    const offersStep = OFFERS_INTERNAL_STEP_BY_SLUG[insuranceSlug];
    const isActive =
      quoteCalculationActiveRef.current ||
      formDataRef.current.quoteRecalculationInProgress === true;
    if (!isActive || offersStep == null) return false;

    cancelQuoteSubmit();
    cancelOngoingRequests();
    endOffersRecalculation();
    isLoadingRef.current = false;
    setIsLoadingState(false);
    setApiError(null);
    setFormData(prev => ({
      ...prev,
      quoteRecalculationInProgress: false,
      completedQuoteResponse: null,
      initialQuoteResponse: null,
      selectedOffer: null,
      selectedProviders: [],
    }));
    setStep(offersStep - 1);
    return true;
  }, [pathname, cancelOngoingRequests, endOffersRecalculation, setFormData, setStep]);

  // Strategy 3: Clear storage for new quote
  const clearStorageForNewQuoteHandler = useCallback(() => {
    if (pathname && pathname.includes('/skaiciuokle/')) {
      const pathParts = pathname.split('/');
      const insuranceSlug = pathParts[pathParts.length - 1] || 'default';
      clearStorageForNewQuote(insuranceSlug);
      // Reset formData to defaults
      setFormData({
        duration: '12',
        usagePurpose: 'NONE',
      });
      // Reset step to 0
      setStep(0);
    }
  }, [pathname, setFormData, setStep]);

  // Note: We can't easily memoize the context value because formData and setFormData
  // change on every update. However, components should use stable dependencies in their useEffects.
  return (
    <InsuranceContext.Provider
      value={{
        step,
        setStep,
        formData,
        setFormData,
        selfServiceAccountEmail,
        apiError,
        setApiError,
        isRetrying,
        setIsRetrying,
        isLoading,
        setIsLoading,
        retryApiRequest,
        stepSubmitHandler,
        setStepSubmitHandler,
        validationErrors,
        setValidationErrors,
        cancelOngoingRequests,
        cancelActiveQuoteCalculation,
        createAbortController,
        getAbortSignal,
        beginOffersRecalculation,
        endOffersRecalculation,
        cancelOffersRecalculation,
        allowCalculatorUnload,
        clearStorageForNewQuote: clearStorageForNewQuoteHandler,
        insuranceData: providedInsuranceData, // Contains seoContent and calculatorLink from Contentful
      }}
    >
      {children}
    </InsuranceContext.Provider>
  );
}

export function useOptionalInsurance() {
  return useContext(InsuranceContext);
}

export function useInsurance() {
  const context = useOptionalInsurance();
  if (!context) {
    throw new Error('useInsurance must be used within an InsuranceProvider');
  }
  return context;
}
